const settings = {
  title: 'Settings',
  subtitle: 'Configure your account and platform preferences',
  categoriesTitle: 'Settings Categories',
  badges: {
    comingSoon: 'Coming Soon',
    restricted: 'Restricted',
  },
  statusLabels: {
    configured: 'Configured',
    incomplete: 'Incomplete',
    pending: 'Not Configured',
  },
  sections: {
    profile: {
      title: 'Profile Settings',
      description: 'Manage your account information and contact details',
      placeholderTitle: 'Profile settings will be implemented here.',
      placeholderDescription:
        'This will include account information, contact details, and profile updates.',
    },
    organization: {
      title: 'Organization Settings',
      description: 'Company information and team management',
      placeholderTitle: 'Organization settings will be implemented here.',
      placeholderDescription:
        'This will include company information and team member management.',
    },
    stripe: {
      title: 'Stripe Configuration',
      description: 'Configure payment processing with Stripe',
    },
    socialAuth: {
      title: 'Social Login',
      description: 'Manage OAuth providers for Google, GitHub, Apple, and Microsoft sign-in',
    },
    signupRedirect: {
      title: 'Signup Redirect',
      description: 'Send self-serve signups to a custom destination after account creation',
    },
    providerPassword: {
      title: 'Provider password login',
      description: 'Enable and configure password requirements for provider admins.',
    },
    customerPassword: {
      title: 'Customer password login',
      description: 'Control whether customers can sign in with passwords.',
    },
    integrations: {
      title: 'Integrations',
      description: 'Connect external platforms and automation targets.',
    },
    general: {
      title: 'General',
      description: 'Core account settings and legal links.',
    },
    portalMenu: {
      title: 'Customer portal menu',
      description: 'Add custom links to the customer portal navigation.',
    },
    checkout: {
      title: 'Checkout',
      description: 'Configure checkout copy, fields, and domains.',
    },
    branding: {
      title: 'Branding & White-Label',
      description: 'Customize your customer-facing experience',
    },
    notifications: {
      title: 'Notification Preferences',
      description: 'Control platform notifications and alerts',
    },
    apiAccess: {
      title: 'Provider API Access',
      description: 'Manage API tokens and secure customer SSO links',
    },
    testMode: {
      title: 'Test Mode',
      description: 'Test API products before going live',
    },
  },
  account: 'Account',
  preferences: 'Preferences',
  security: 'Security',
  notifications: 'Notifications',
  signupRedirect: {
    title: 'Signup redirect',
    description: 'Control where self-serve customers land after registering with your domain.',
    fields: {
      enabled: 'Enable redirect',
      url: 'Redirect URL',
      verificationEnabled: 'Enable verification redirect',
      verificationUrl: 'Verification redirect URL',
    },
    helpers: {
      enabled:
        'Applies only to unauthenticated self-signup flows. Provider-created customers skip this redirect.',
      url: 'Use a full URL including protocol, for example https://app.example.com/welcome.',
      scope: 'Disabling the redirect clears the stored URL.',
      verification: 'After email verification, customers are sent here; if blank, the signup redirect (if set) is used.',
      verificationToggle: 'Enable a distinct redirect after email verification.',
      verificationUrl: 'Use a full URL including protocol. A redirect_token is appended.',
    },
    placeholders: {
      url: 'https://app.example.com/after-signup',
      verificationUrl: 'https://app.example.com/after-verification',
    },
    actions: {
      save: 'Save redirect settings',
      saving: 'Saving...',
      retry: 'Try again',
    },
    messages: {
      saveSuccess: 'Signup redirect settings updated.',
      saveError: 'Unable to update signup redirect settings.',
      loadError: 'Unable to load signup redirect settings.',
    },
    validation: {
      required: 'Enter a redirect URL to enable this setting.',
      url: 'Enter a valid URL (including https://).',
      max: 'Redirect URL must be 2048 characters or fewer.',
    },
    sections: {
      signup: 'Redirect after signup',
      verification: 'Redirect after verification',
    },
  },
  branding: {
    title: 'Branding',
    description: 'Customize your customer-facing brand experience.',
    fields: {
      active: 'Enable branding',
      logoDarkUrl: 'Dark logo URL',
      logoLightUrl: 'Light logo URL',
      logoUrl: 'Fallback logo URL',
      primaryColor: 'Primary color',
      accentColor: 'Accent color',
    },
    helpers: {
      active: 'When enabled, your logo and colors appear on customer-facing pages.',
      logoDarkUrl: 'Shown on light mode backgrounds.',
      logoLightUrl: 'Shown on dark mode backgrounds.',
      logoUrl: 'Used when a theme-specific logo is not provided.',
      primaryColor: 'Hex color used for primary actions and highlights.',
      accentColor: 'Hex color used for secondary actions and accents.',
    },
    placeholders: {
      logoDarkUrl: 'https://cdn.example.com/brand/logo-dark.png',
      logoLightUrl: 'https://cdn.example.com/brand/logo-light.png',
      logoUrl: 'https://cdn.example.com/brand/logo.png',
      primaryColor: '#0f172a',
      accentColor: '#22c55e',
    },
    validation: {
      logoTooLong: 'Logo URL must be 2048 characters or fewer.',
      colorInvalid: 'Enter a valid hex color like #0f172a.',
    },
    actions: {
      save: 'Save branding',
    },
    messages: {
      saveSuccess: 'Branding settings saved.',
      saveError: 'Unable to save branding settings.',
      loadError: 'Unable to load branding settings.',
    },
    preview: {
      title: 'Preview',
      description: 'Preview how your branding will appear.',
      lightLabel: 'Light mode',
      darkLabel: 'Dark mode',
      primary: 'Primary',
      accent: 'Accent',
      logoAlt: '{{provider}} logo',
      urlPlaceholder: 'Provider URL unavailable.',
      defaultName: 'Provider',
    },
  },
  terms: {
    title: 'Terms & Conditions',
    description: 'Provide a link to your Terms & Conditions page for customer flows.',
    helper: "Link users to your website's Terms & Conditions. HTTPS only; leave blank to disable.",
    loadError: 'Unable to load Terms & Conditions settings.',
    saveSuccess: 'Terms & Conditions link saved.',
    saveError: 'Unable to save Terms & Conditions link.',
    fields: {
      link: 'Terms & Conditions URL',
    },
    placeholders: {
      link: 'https://example.com/terms',
    },
    validation: {
      invalid: 'Enter a valid HTTPS URL.',
      tooLong: 'Terms URL must be shorter than 2048 characters.',
    },
    actions: {
      save: 'Save link',
    },
  },
  customScripts: {
    title: 'Custom scripts',
    description: 'Inject custom HTML or scripts into every customer and provider page.',
    helper: 'Injected into the page head without sanitization. Leave blank to remove.',
    fields: {
      script: 'Custom scripts',
    },
    placeholders: {
      script: "<script>console.log('custom')</script>",
    },
    actions: {
      save: 'Save custom scripts',
    },
    messages: {
      saveSuccess: 'Custom scripts saved.',
      saveError: 'Unable to save custom scripts.',
      loadError: 'Unable to load custom scripts.',
    },
  },
  portalMenu: {
    title: 'Customer portal menu',
    description: 'Add custom links to the customer portal navigation.',
    helper: 'Links appear in the customer portal sidebar. Only http/https URLs are supported.',
    hints: 'Lower order numbers appear first. Use icon names like help-circle.',
    iconLibrary: 'Icon names come from Lucide (lucide-react). Browse:',
    iconLibraryUrl: 'https://lucide.dev/icons',
    iconLibraryLinkLabel: 'lucide.dev/icons',
    empty: 'No menu items yet. Add up to 20 links.',
    itemTitle: 'Menu item {{index}}',
    fields: {
      label: 'Label',
      url: 'URL',
      icon: 'Icon',
      order: 'Order',
      target: 'Target',
    },
    placeholders: {
      label: 'Support',
      url: 'https://support.example.com',
      icon: 'help-circle',
      order: '10',
      target: 'Open in new tab',
    },
    targets: {
      blank: 'Open in new tab',
      self: 'Open in same tab',
    },
    actions: {
      add: 'Add menu item',
      clear: 'Clear menu',
      save: 'Save menu',
    },
    messages: {
      saveSuccess: 'Customer portal menu saved.',
      saveError: 'Unable to save customer portal menu.',
      loadError: 'Unable to load customer portal menu.',
    },
    validation: {
      maxItems: 'You can add up to 20 menu items.',
      labelRequired: 'Enter a label.',
      labelTooLong: 'Label must be 80 characters or fewer.',
      urlRequired: 'Enter a URL.',
      urlTooLong: 'URL must be 2048 characters or fewer.',
      urlInvalid: 'Enter a valid http(s) URL.',
      iconTooLong: 'Icon must be 80 characters or fewer.',
      orderRange: 'Order must be between 0 and 10000.',
      orderInteger: 'Order must be a whole number.',
    },
  },
  checkoutDomain: {
    title: 'Base domain',
    description:
      'Enter a base domain to generate the checkout and authentication subdomains.',
    helper: 'Use a host only (no https://, no path, no port). Enter the base domain.',
    helperDerived: 'Point checkout.* and auth.* to the Florentin application.',
    helpers: {
      enabled: 'Use the custom checkout and auth subdomains when enabled.',
      dnsLinksLabel: 'DNS guides:',
      cloudflare: 'Cloudflare',
      godaddy: 'GoDaddy',
      namecheap: 'Namecheap',
    },
    preview: {
      checkout: 'Checkout:',
      checkoutSuffix: '(Used for checkout)',
      auth: 'Auth:',
      authSuffix: '(Used for login)',
    },
    fields: {
      domain: 'Base domain',
      enabled: 'Enable custom domains',
    },
    placeholders: {
      domain: 'acme.com',
    },
    validation: {
      invalid: 'Enter a valid host (no scheme, no path, no port).',
      required: 'Enter a base domain to enable custom domains.',
      tooLong: 'Checkout domain must be 255 characters or fewer.',
    },
    actions: {
      save: 'Save checkout domain',
    },
    messages: {
      saveSuccess: 'Checkout domain settings updated.',
      saveError: 'Unable to update checkout domain settings.',
      loadError: 'Unable to load checkout domain settings.',
    },
  },
  notificationSettings: {
    title: 'Email notifications',
    description: 'Configure sender details and BCC recipients for outgoing emails.',
    sender: {
      title: 'Sender details',
      helper: 'Shown in the From field for customer emails.',
    },
    helper: 'Add up to 10 BCC recipients for OTPs, invitations, verification, and billing emails.',
    hint: 'Press Enter or comma to add another address.',
    empty: 'No BCC recipients added yet.',
    loadError: 'Unable to load notification settings.',
    saveSuccess: 'Notification settings saved.',
    saveError: 'Unable to save notification settings.',
    count: '{{count}} / {{max}} added',
    fields: {
      fromName: 'From name',
      fromAddress: 'From email',
      bcc: 'BCC recipients',
    },
    placeholders: {
      fromName: 'Acme',
      fromAddress: 'no-reply@acme.com',
      bcc: 'audit@example.com',
    },
    validation: {
      invalidEmail: 'Enter a valid email address.',
      tooMany: 'You can add up to 10 BCC recipients.',
      tooLong: 'Email addresses must be 255 characters or fewer.',
      nameTooLong: 'From name must be 255 characters or fewer.',
      fromRequired: 'Enter a from email address.',
    },
    actions: {
      save: 'Save email settings',
    },
  },
  checkoutContent: {
    title: 'Checkout messaging',
    description: 'Customize the headline and bullets shown on your checkout page.',
    loadError: 'Unable to load checkout messaging.',
    saveSuccess: 'Checkout messaging saved.',
    saveError: 'Unable to save checkout messaging.',
    fields: {
      headline: 'Headline',
      subheadline: 'Subheadline',
      bullet: 'Bullet',
    },
    helper: {
      headline: 'Shown as the main title on checkout.',
      subheadline: 'Brief supporting copy below the headline.',
    },
    placeholders: {
      headline: 'Launch faster with confidence',
      subheadline: 'Everything you need to onboard and scale securely.',
      bullet1: 'Start building with a secure, fast API platform.',
      bullet2: 'Transparent pricing with predictable renewals.',
      bullet3: 'Priority support to help you launch faster.',
    },
    validation: {
      headlineTooLong: 'Headline must be 120 characters or fewer.',
      subheadlineTooLong: 'Subheadline must be 200 characters or fewer.',
      bulletTooLong: 'Bullets must be 160 characters or fewer.',
    },
    actions: {
      save: 'Save messaging',
    },
  },
  checkoutFields: {
    title: 'Checkout fields',
    description: 'Choose which fields appear on checkout and which are required.',
    helper: 'Drag rows to reorder checkout fields.',
    columns: {
      field: 'Field',
      enabled: 'Enabled',
      required: 'Required',
      reorder: 'Reorder',
    },
    fields: {
      company: 'Company',
      contact_phone: 'Contact phone',
      billing: {
        address_line_1: 'Billing address line 1',
        address_line_2: 'Billing address line 2',
        address_city: 'Billing city',
        address_state: 'Billing state or region',
        address_postal_code: 'Billing postal code',
        address_country: 'Billing country',
      },
    },
    actions: {
      save: 'Save fields',
    },
    messages: {
      saveSuccess: 'Checkout fields updated.',
      saveError: 'Unable to update checkout fields.',
      loadError: 'Unable to load checkout fields.',
    },
  },
  socialAuth: {
    title: 'Social OAuth Providers',
    description: 'Configure OAuth credentials for provider and customer portals.',
    status: {
      disabled: 'Disabled',
      valid: 'Validated',
      invalid: 'Invalid',
      pending: 'Not Validated',
      neverValidated: 'Never validated',
    },
    providers: {
      google: {
        name: 'Google',
        title: 'Google Sign-In',
        description: 'Allow users to sign in with Google Accounts using OAuth 2.0.',
      },
      github: {
        name: 'GitHub',
        title: 'GitHub OAuth Apps',
        description: 'Use GitHub OAuth Apps to authenticate providers or customers.',
      },
      apple: {
        name: 'Apple',
        title: 'Sign in with Apple',
        description: 'Configure Apple Services to support Sign in with Apple flows.',
      },
      azure: {
        name: 'Microsoft',
        title: 'Microsoft SSO',
        description: 'Enable Microsoft Entra ID (Azure AD) sign-in for provider and customer portals.',
      },
    },
    fields: {
      enabled: 'Enable provider',
      mode: {
        label: 'Environment',
        test: 'Test Mode',
        live: 'Live Mode',
      },
      clientId: 'Client ID',
      clientSecret: 'Client Secret',
      teamId: 'Team ID',
      keyId: 'Key ID',
      privateKey: 'Private Key',
      autoRotateSecret: 'Auto-rotate client secret',
      enterpriseHost: 'Enterprise Host',
      orgWhitelist: 'Organization whitelist',
      scopes: 'Scopes',
      prompt: 'Prompt',
      loginHint: 'Default login hint',
      redirectOverride: 'Redirect URI override',
    },
    placeholders: {
      clientId: 'Enter client ID',
      clientSecret: 'Enter client secret',
      scopes: 'openid email profile',
      prompt: 'select_account',
      loginHint: 'user@example.com',
      redirectOverride: 'https://example.com/auth/oauth/callback',
      orgWhitelist: 'acme\nbeta-company',
    },
    helpers: {
      enabled: 'Disable this provider to hide it from the login pages.',
      mode: 'Choose test mode while integrating; switch to live after validation.',
      clientSecret: 'Leave blank to keep the existing secret.',
      privateKey: 'Paste the .p8 private key contents from Apple Developer.',
      autoRotateSecret: 'Automatically generate Sign in with Apple client secrets before they expire.',
      orgWhitelist: 'One organization per line. Users must belong to the listed organizations.',
      scopes: 'Separate by commas or new lines. Leave blank to use the default scopes.',
      redirectOverride: 'Optional. Supply a custom redirect URI if different from the default.',
      redirectInfo: 'Share this redirect URI with your identity provider configuration.',
      clientIdMasked: 'Current client ID: {{value}}',
    },
    labels: {
      lastValidated: 'Last validated: {{date}}',
      currentRedirect: 'Current redirect URI',
      outstandingIssues: 'Outstanding issues',
      correlationId: 'Correlation ID: {{id}}',
    },
    actions: {
      save: 'Save configuration',
      validate: 'Validate credentials',
      validating: 'Validating...',
      test: 'Test connection',
      testing: 'Testing...',
      delete: 'Delete configuration',
      deleting: 'Deleting...',
      copyRedirect: 'Copy redirect',
    },
    statusBadges: {
      ready: 'Ready',
    },
    messages: {
      saveSuccess: 'OAuth settings saved successfully.',
      saveError: 'Unable to save OAuth settings.',
      validationSuccess: 'Credentials are valid.',
      validationError: 'Validation failed. Check the credentials and try again.',
      testSuccess: 'Connection test succeeded.',
      testError: 'Connection test failed.',
      deleteSuccess: 'OAuth provider disabled and secrets cleared.',
      deleteError: 'Unable to disable this provider.',
      confirmDelete: 'Disable {{provider}} and clear stored secrets? You will need to re-enter credentials to re-enable it.',
      copiedRedirect: 'Redirect URI copied to clipboard.',
      loadFailed: 'Unable to load OAuth settings.',
      forbidden: 'You do not have permission to manage OAuth settings. Check feature flags or user roles.',
      noProviders: 'No OAuth providers are available for this tenant.',
    },
  },
  customerSync: {
    title: 'Integrations',
    description: 'Manage automatic exports of newly verified customers to external platforms.',
    toggleLabel: 'Sync verified customers to external platform',
    toggleDescription:
      'Automatically push newly verified customers to your connected third-party platform.',
    enabledNote: 'Requires integration credentials configured by your support team.',
    addConfigFirst: 'Add at least one integration configuration before enabling sync.',
    loadError: 'Unable to load customer sync settings. Refresh and try again later.',
    configLoadError: 'Unable to load integration configurations. Refresh and try again later.',
    forbidden: 'You do not have permission to manage customer sync settings.',
    saveSuccess: 'Sync preference updated.',
    saveError: 'Unable to update sync settings. Try again.',
    addConfig: 'Add configuration',
    configsTitle: 'Integration configurations',
    configsDescription: 'Verified customers are sent to every enabled configuration.',
    emptyTitle: 'No integrations configured yet',
    emptyDescription: 'Create at least one webhook configuration to turn on customer syncing.',
    createDialogTitle: 'Add integration configuration',
    editDialogTitle: 'Edit integration configuration',
    dialogDescription: 'This webhook triggers each time a new customer is verified.',
    createDialogCta: 'Create configuration',
    updateDialogCta: 'Save changes',
    createSuccess: 'Integration configuration created.',
    updateSuccess: 'Integration configuration updated.',
    deleteTitle: 'Delete this configuration?',
    deleteDescription: 'Verified customer payloads will no longer be sent to this endpoint.',
    confirmDelete: 'Delete configuration',
    deleteSuccess: 'Configuration removed.',
    deleteError: 'Unable to delete this configuration.',
    invalidJson: 'Enter a valid JSON object.',
    timeoutRangeError: 'Timeout must be between 1 and 120 seconds.',
    status: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    table: {
      name: 'Name',
      endpoint: 'Endpoint',
      method: 'Method',
      status: 'Status',
      updated: 'Updated',
      actions: 'Actions',
      never: 'Never',
    },
    form: {
      nameLabel: 'Configuration name',
      namePlaceholder: 'e.g. Customer sync webhook',
      endpointLabel: 'Endpoint URL',
      methodLabel: 'HTTP method',
      timeoutLabel: 'Timeout (seconds)',
      timeoutDescription: 'Florentin aborts the request if no response is received within this window.',
      headersLabel: 'Headers (JSON)',
      headersDescription: 'Optional. Provide additional headers such as Authorization tokens.',
      payloadLabel: 'Payload template (JSON)',
      payloadDescription:
        'Use {{email}}, {{first_name}}, {{last_name}}, or {{florentin_customer_id}} placeholders to map Florentin fields.',
      enabledLabel: 'Enable this configuration',
      enabledDescription: 'Disabled configurations remain saved but will not receive customer events.',
    },
  },
  webhooks: {
    title: 'Webhook endpoints',
    description: 'Manage webhook destinations and inspect recent delivery attempts.',
    helper: 'Each enabled endpoint receives subscribed events and can use a shared secret signature.',
    add: 'Add webhook',
    loadError: 'Unable to load webhook endpoints.',
    forbidden: 'You do not have permission to manage webhooks.',
    emptyTitle: 'No webhook endpoints configured yet',
    emptyDescription: 'Create at least one endpoint to start receiving webhook events.',
    createDialogTitle: 'Add webhook endpoint',
    editDialogTitle: 'Edit webhook endpoint',
    dialogDescription: 'Configure where Florentin will deliver selected webhook events.',
    createDialogCta: 'Create webhook',
    updateDialogCta: 'Save changes',
    createSuccess: 'Webhook endpoint created.',
    updateSuccess: 'Webhook endpoint updated.',
    saveError: 'Unable to save webhook endpoint.',
    deleteTitle: 'Delete this webhook endpoint?',
    deleteDescription: 'Future events will no longer be delivered to this URL.',
    confirmDelete: 'Delete webhook',
    deleteSuccess: 'Webhook endpoint removed.',
    deleteError: 'Unable to delete this webhook endpoint.',
    viewDeliveries: 'View deliveries',
    hideDeliveries: 'Hide deliveries',
    status: {
      enabled: 'Enabled',
      disabled: 'Disabled',
      secretConfigured: 'Secret configured',
      customHeadersConfigured: 'Custom headers',
    },
    table: {
      name: 'Name',
      url: 'Endpoint URL',
      events: 'Events',
      timeout: 'Timeout',
      status: 'Status',
      updated: 'Updated',
      actions: 'Actions',
      never: 'Never',
      eventsCount: '{{count}} selected',
      timeoutSeconds: '{{count}}s',
    },
    form: {
      nameLabel: 'Name',
      namePlaceholder: 'Primary endpoint',
      urlLabel: 'Endpoint URL',
      secretLabel: 'Signing secret',
      secretPlaceholder: 'Enter a secret to sign webhook payloads',
      secretDescription: 'Optional. Leave blank to keep the current secret on update.',
      secretConfigured: 'A secret is currently configured.',
      clearSecretLabel: 'Clear stored secret',
      clearSecretDescription: 'When enabled, Florentin will remove the existing secret for this endpoint.',
      headersLabel: 'Custom headers (JSON)',
      headersDescription: 'Optional. Add static headers for outbound requests (for example n8n header auth).',
      headersConfigured: 'Header values are hidden for security. Enter new values to replace.',
      headerKeys: 'Keys: {{keys}}',
      headersJsonError: 'Enter a valid JSON object for headers.',
      clearHeadersLabel: 'Clear custom headers',
      clearHeadersDescription: 'When enabled, Florentin will remove all stored custom headers for this endpoint.',
      eventsLabel: 'Subscribed events',
      timeoutLabel: 'Timeout (seconds)',
      timeoutDescription: 'Requests are aborted when no response is received within this window.',
      enabledLabel: 'Enabled',
      enabledDescription: 'Disabled endpoints remain saved but will not receive events.',
    },
    events: {
      'account.created': 'account.created',
      'user.login': 'user.login',
      'subscription.purchased': 'subscription.purchased',
      'invoice.created': 'invoice.created',
      'subscription.upgraded': 'subscription.upgraded',
      'subscription.downgraded': 'subscription.downgraded',
      'subscription.cancelled': 'subscription.cancelled',
      'team_member.added': 'team_member.added',
    },
    deliveryStatus: {
      pending: 'Pending',
      processing: 'Processing',
      succeeded: 'Succeeded',
      failed: 'Failed',
    },
    deliveries: {
      title: 'Recent deliveries',
      loadError: 'Unable to load delivery logs.',
      empty: 'No deliveries found for this endpoint yet.',
      attempt: 'Attempt {{count}}',
      responseStatus: 'Response status',
      dispatchedAt: 'Dispatched at',
      deliveredAt: 'Delivered at',
      nextRetryAt: 'Next retry at',
      errorMessage: 'Error message',
      responseBody: 'Response body',
      payload: 'Payload',
    },
  },
  externalPlanSelection: {
    title: 'External partner plan selection',
    description:
      'Configure allowed partner domains and shared secret used for external plan selection requests.',
    loadError: 'Unable to load partner settings. Check your session or try again later.',
    saveSuccess: 'External partner settings saved.',
    saveError: 'Unable to update partner settings. Try again.',
    rotateSuccess: 'Secret rotated. Copy it now.',
    rotateError: 'Unable to rotate the partner secret.',
    copySuccess: 'Secret copied.',
    copyError: 'Unable to copy the secret to clipboard.',
    secretOneTime: 'Copy the new shared secret now',
    copySecret: 'Copy secret',
    status: {
      actionNeeded: 'Partner endpoint is not ready.',
      ready: 'Integration is ready',
      disabled: 'Integration is disabled. Enable it to accept partner calls.',
      missingSecret: 'No active shared secret found. Rotate to issue a new secret.',
      healthy:
        'Partner domain and secret are configured. Incoming partner requests will be accepted.',
    },
    fields: {
      partnerName: 'Partner name',
      enabled: 'Enable partner integration',
      allowedDomains: 'Allowed domains',
      secretPreview: 'Secret preview',
      lastRotatedAt: 'Last rotated',
    },
    helpers: {
      enabled: 'When disabled, partner requests will be rejected.',
      allowedDomains: 'One hostname per line. Protocols and paths are stripped automatically.',
      secretPreview: 'Rotate to view the full shared secret.',
      rotate: 'You will receive the full secret immediately after rotation.',
    },
    placeholders: {
      partnerName: 'Acme Partner',
      allowedDomains: 'partner.example.com\\napi.partner.example.com',
    },
    validation: {
      partnerName: 'Partner name is required.',
      allowedDomains: 'Enter valid hostnames without protocols or paths.',
    },
    badges: {
      enabled: 'Enabled',
      disabled: 'Disabled',
      secret: 'Shared secret',
    },
    actions: {
      rotate: 'Rotate secret',
      save: 'Save changes',
    },
    metrics: {
      title: 'External plan selection usage',
      totalIntents: 'Total intents',
      fulfilledWithoutPayment: 'Fulfilled without payment',
      pendingPayment: 'Pending payment',
      completed: 'Completed',
      lastIntentAt: 'Last intent at',
    },
  },
  passwordAuth: {
    title: 'Provider password login',
    customerTitle: 'Customer password login',
    description:
      'Allow provider users to sign in with email and password in addition to magic links and OAuth providers.',
    providerDescription:
      'Allow provider users to sign in with email and password in addition to magic links and social logins.',
    customerDescription:
      'Allow customers to sign in to their portal with email and password in addition to magic links.',
    enableLabel: 'Enable password login',
    enableDescription: 'Adds an email + password form to the provider login page.',
    customerEnableDescription: 'Adds an email + password form to the customer login page.',
    policyTitle: 'Password policy',
    policyDescription: 'Define the minimum requirements passwords must meet for your team.',
    minLengthLabel: 'Minimum length',
    historyLabel: 'Disallow previous passwords',
    historyDescription: 'How many previous passwords each user cannot reuse.',
    requireNumbers: 'Require numbers',
    requireNumbersDescription: 'Password must include at least one number.',
    requireSymbols: 'Require symbols',
    requireSymbolsDescription: 'Password must include at least one punctuation or symbol character.',
    requireMixedCase: 'Require mixed case',
    requireMixedCaseDescription: 'Password must include both uppercase and lowercase letters.',
    policyDisabledHint: 'Password rules apply only when password login is enabled.',
    loadError: 'Unable to load password login settings. Refresh or try again later.',
    saveSuccess: 'Password login settings saved.',
    saveError: 'Unable to update password login configuration. Please try again.',
    saving: 'Saving…',
    saveCta: 'Save configuration',
    unauthorized:
      'Unable to load password settings with this session. Provide a Provider PAT via VITE_PROVIDER_AUTH_SETTINGS_TOKEN or store one under florentin.provider.auth_settings_token.',
  },
  providerApi: {
    description: 'Generate service tokens to automate customer workflows and issue secure portal links.',
    summary: 'Tokens inherit your provider permissions. Only share them with trusted infrastructure.',
    securityNotice:
      'Treat API tokens like passwords. Rotate regularly, store them in a secure vault, and revoke any token that might be exposed.',
    createToken: 'Generate service token',
    ssoConfigTitle: 'Customer SSO defaults',
    ssoConfigDescription: 'Defaults applied when issuing one-click links for synced customers.',
    ssoDefaultTtl: 'Default link lifetime',
    minutes: '{{count}} minute',
    minutes_plural: '{{count}} minutes',
    ssoMaxTtl: 'Maximum override: {{count}} minutes',
    ssoDomains: 'Allowed redirect domains',
    noDomains: 'No redirect domains configured. Links will use the provider primary domain.',
    ssoOverridesDisabled: 'Runtime overrides are disabled. Links will always use these defaults.',
    ssoConfigError: 'Unable to load SSO configuration. Generated tokens will still function.',
    tokensTitle: 'Service tokens',
    tokensDescription: 'List of issued tokens. Revoke unused tokens to reduce risk.',
    tokensError: 'Failed to load tokens.',
    noTokens: 'No service tokens yet. Generate one to enable external automation.',
    noTokensForScope: 'No tokens found for this scope.',
    scopeFilterLabel: 'Scope',
    scopeFilterAll: 'All scopes',
    scopes: {
      ssoIssue: {
        label: 'Issue customer portal links',
        description: 'Allows this token to mint single-use SSO links for synced customers.',
      },
      providerAccess: {
        label: 'Provider API access',
        description: 'Grants programmatic access to the Provider API endpoints for automation.',
      },
    },
    columns: {
      label: 'Label',
      scopes: 'Scopes',
      created: 'Created',
      lastUsed: 'Last used',
      actions: 'Actions',
    },
    expiresLabel: 'Expires {{date}}',
    revoke: 'Revoke',
    revokeTitle: 'Revoke token?',
    revokeDescription:
      'Revoking immediately invalidates this token. Any connected systems will lose access.',
    revokeConfirm: 'Revoke token',
    revokeSuccess: 'Token revoked.',
    revokeError: 'Unable to revoke token.',
    restricted: 'Only provider admins can manage API access.',
    tokenForm: {
      title: 'Generate service token',
      subtitle: 'Tokens are shown once. Copy and store them securely in your secrets manager.',
      label: 'Token label',
      labelPlaceholder: 'e.g., Make Zapier sync',
      labelTooShort: 'Label must be at least 3 characters.',
      labelTooLong: 'Label cannot exceed 120 characters.',
      scopes: 'Scopes',
      scopesRequired: 'Select at least one scope.',
      notes: 'Internal note',
      notesPlaceholder: 'Optional details about where this token is used or stored.',
      expiry: {
        label: 'Expiry',
        never: 'No expiry (manual revoke required)',
        oneHour: '1 hour',
        oneDay: '24 hours',
        oneWeek: '7 days',
        thirtyDays: '30 days',
      },
      submit: 'Create token',
      creating: 'Issuing token…',
      creationError: 'Unable to issue token.',
      copyAction: 'Copy token',
      copiedAction: 'Copied',
      copied: 'Token copied to clipboard.',
      copyFailed: 'Unable to copy token.',
      resultNotice: 'Copy this token now. You will not be able to view it again.',
      resultNoExpiry: 'This token never expires automatically.',
      resultExpiry: 'Expires on {{date}}',
    },
    unavailable: 'Provider API access will be available once your backend enables the tokens feature flag.',
    toggleNotice: 'Set VITE_ENABLE_PROVIDER_API_TOKENS=true in the provider UI environment after the API endpoints are live.',
  },
} as const;

export default settings;
